import React, { Fragment, useState, useEffect } from 'react'
import numbro from 'numbro'
import {
  List,
  Datagrid,
  TextField,
  downloadCSV,
  FunctionField,
  Pagination,
  useDataProvider,
} from 'react-admin'

import Accordion from '@material-ui/core/Accordion'
import AccordionSummary from '@material-ui/core/AccordionSummary'
import AccordionDetails from '@material-ui/core/AccordionDetails'
import Typography from '@material-ui/core/Typography'
import jsonExport from 'jsonexport/dist'
import MuiList from '@material-ui/core/List'
import Box from '@material-ui/core/Box'
import ListItem from '@material-ui/core/ListItem'
import ListItemText from '@material-ui/core/ListItemText'
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction'
import { makeStyles } from '@material-ui/core/styles'
import Card from '@material-ui/core/Card'
import AccessTimeIcon from '@material-ui/icons/AccessTime'
import CardContent from '@material-ui/core/CardContent'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import Button from '@material-ui/core/Button'
import FormControl from '@material-ui/core/FormControl'
import InputLabel from '@material-ui/core/InputLabel'
import MenuItem from '@material-ui/core/MenuItem'
import Select from '@material-ui/core/Select'
import Chip from '@material-ui/core/Chip'
import IconButton from '@material-ui/core/IconButton'
import Menu from '@material-ui/core/Menu'
import MuiTextField from '@material-ui/core/TextField'
import FilterListIcon from '@material-ui/icons/FilterList'
import CloseIcon from '@material-ui/icons/Close'
import Divider from '@material-ui/core/Divider'
import UsageChart from '../components/UsageChart'
import Tooltip from '../components/Tooltip'
import Data from '../components/Data'
import { formatHistoryData } from '../lib/utils'
import * as api from '../data'
import * as storage from '../lib/storage'
import {
  ResponsiveContainer,
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
} from 'recharts'

const useStyles = makeStyles((theme) => ({
  textInput: { width: 400, marginRight: 10 },
  selectInput: { width: 200 },
  filterPanel: {
    marginBottom: 12,
  },
  searchRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginTop: 16,
  },
  searchField: {
    flex: '1 1 280px',
    minWidth: 280,
  },
  filterActions: {
    display: 'flex',
    gap: 12,
    marginTop: 16,
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
  },
  addFilterRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginLeft: 'auto',
  },
  chipsRow: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    marginTop: 12,
  },
  tagSelect: {
    minWidth: 220,
  },
  removableFilter: {
    position: 'relative',
    display: 'inline-flex',
  },
  removeFilterButton: {
    position: 'absolute',
    top: -8,
    right: -8,
    zIndex: 1,
    backgroundColor: '#f1f3f4',
    border: '1px solid rgba(0, 0, 0, 0.12)',
    padding: 4,
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
  },
  chip: {
    margin: 2,
  },
  field: {
    display: 'flex',
  },
  actions: {
    display: 'flex',
  },
  indicator: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  projectionSection: {
    display: 'flex',
    gap: 24,
    alignItems: 'stretch',
    flexWrap: 'wrap',
  },
  projectionCard: {
    minWidth: 320,
    flex: '0 0 340px',
    borderRadius: 12,
    border: '1px solid #e6eaf0',
    boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
  },
  projectionChartCard: {
    minWidth: 360,
    flex: '1 1 520px',
    borderRadius: 12,
    border: '1px solid #e6eaf0',
    boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
  },
  projectionHeaderEyebrow: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  projectionTitle: {
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(2),
  },
  projectionStats: {
    display: 'flex',
    gap: 24,
    marginBottom: theme.spacing(2),
  },
  projectionStatLabel: {
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
  projectionStatValue: {
    fontWeight: 600,
  },
  projectionList: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  projectionRow: {
    paddingLeft: 0,
    paddingRight: 0,
    borderBottom: '1px solid #eef2f7',
  },
  projectionRowLast: {
    borderBottom: 'none',
  },
  projectionPrimary: {
    fontWeight: 600,
  },
  projectionChartWrap: {
    width: '100%',
    height: 300,
  },
  projectionEmpty: {
    padding: theme.spacing(2, 0),
    color: theme.palette.text.secondary,
  },
  tooltipContent: {
    backgroundColor: '#fff',
    borderRadius: 4,
    paddingBottom: 16,
    marginBottom: 8,
    marginTop: 8,
    minHeight: 200,
    color: '#999',
    fontSize: 14,
    justifyContent: 'center',
    display: 'flex',
    alignItems: 'center',
  },

  tooltipContentSmall: {
    backgroundColor: '#fff',
    borderRadius: 4,
    marginBottom: 8,
    marginTop: 8,
    minHeight: 100,
    color: '#666',
    fontSize: 14,
    justifyContent: 'center',
    display: 'flex',
    alignItems: 'center',
  },
  bold: {
    fontWeight: '500',
    color: theme.palette.secondary.main,
  },
  MuiAccordionRoot: {
    '&:before': {
      display: 'none',
    },
  },
}))

const exporter = (modalities) => {
  const modalitiesForExport = modalities.map((modality) => {
    const {
      dashboardIframeUrl,
      logstreamIframeUrl,
      relationshipId,
      metrics,
      relationship,
      ...modalityForExport
    } = modality
    return modalityForExport
  })

  jsonExport(
    modalitiesForExport,
    {
      headers: [
        'id',
        'displayName',
        'tube',
        'tubeScanSecs',
        'tubeScanSecsUsed',
        'tubeExpectedLife',
        'timeToFailure',
        'dateToFailure',
        'tubeCost',
      ],
    },
    (err, csv) => {
      downloadCSV(csv, 'modalities-tube-analysis')
    }
  )
}

const normalizeFilters = (filters = {}) => {
  return Object.entries(filters).reduce((acc, [key, value]) => {
    if (Array.isArray(value) && value.length > 0) {
      acc[key] = value
    } else if (typeof value === 'string' && value.trim()) {
      acc[key] = value.trim()
    } else if (typeof value === 'number') {
      acc[key] = value
    }

    return acc
  }, {})
}

const hasAppliedFilter = (filters) => Object.keys(normalizeFilters(filters)).length > 0

const FilterPanel = ({
  draftFilters,
  onDraftChange,
  onApply,
  onClear,
  tagTypes,
  tagOptions,
  visibleOptionalFilters,
  onAddOptionalFilter,
  onRemoveOptionalFilter,
}) => {
  const classes = useStyles()
  const [anchorEl, setAnchorEl] = useState(null)
  const availableOptionalFilters = tagTypes.filter(
    (tagType) => !visibleOptionalFilters.includes(`tag_${tagType.name}`)
  )
  const isMenuOpen = Boolean(anchorEl)

  return (
    <Card elevation={2} className={classes.filterPanel}>
      <CardContent>
        <div>
          <Typography variant="h6">Filter Tube Analysis</Typography>
          <Typography variant="body2" color="textSecondary">
            Apply at least one filter before loading results.
          </Typography>
        </div>

        <div className={classes.searchRow}>
          <MuiTextField
            label="Search"
            variant="outlined"
            value={draftFilters.q || ''}
            onChange={(event) => onDraftChange('q', event.target.value)}
            className={classes.searchField}
          />

          <FormControl variant="outlined" className={classes.selectInput}>
            <InputLabel>Active Status</InputLabel>
            <Select
              value={draftFilters.active ?? ''}
              onChange={(event) => onDraftChange('active', event.target.value)}
              label="Active Status"
            >
              <MenuItem value="">Show All</MenuItem>
              <MenuItem value={1}>Only Active</MenuItem>
              <MenuItem value={0}>Only Inactive</MenuItem>
            </Select>
          </FormControl>

          {tagTypes
            .filter((tagType) => visibleOptionalFilters.includes(`tag_${tagType.name}`))
            .map((tagType) => {
              const source = `tag_${tagType.name}`
              const selectedIds = draftFilters[source] || []
              const options = tagOptions[tagType.id] || []

              return (
                <div key={tagType.id} className={classes.removableFilter}>
                  <IconButton
                    size="small"
                    className={classes.removeFilterButton}
                    onClick={() => onRemoveOptionalFilter(source)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                  <FormControl variant="outlined" className={classes.tagSelect}>
                    <InputLabel>{`Tag: ${tagType.name}`}</InputLabel>
                    <Select
                      multiple
                      value={selectedIds}
                      onChange={(event) => onDraftChange(source, event.target.value)}
                      label={`Tag: ${tagType.name}`}
                      renderValue={(selected) => (
                        <div className={classes.chips}>
                          {selected.map((id) => {
                            const option = options.find((tag) => tag.id === id)
                            return (
                              <Chip
                                key={id}
                                label={option ? option.name : id}
                                size="small"
                                className={classes.chip}
                              />
                            )
                          })}
                        </div>
                      )}
                    >
                      {options.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              )
            })}

          <div className={classes.addFilterRow}>
            <IconButton
              onClick={(event) => setAnchorEl(event.currentTarget)}
              disabled={availableOptionalFilters.length === 0}
            >
              <FilterListIcon />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              keepMounted
              open={isMenuOpen}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {availableOptionalFilters.map((tagType) => (
                <MenuItem
                  key={tagType.id}
                  onClick={() => {
                    onAddOptionalFilter(`tag_${tagType.name}`)
                    setAnchorEl(null)
                  }}
                >
                  {`Tag: ${tagType.name}`}
                </MenuItem>
              ))}
            </Menu>
          </div>
        </div>

        <div className={classes.filterActions}>
          <Button
            variant="contained"
            color="primary"
            onClick={onApply}
            disabled={!hasAppliedFilter(draftFilters)}
          >
            Apply Filters
          </Button>
          <Button variant="outlined" onClick={onClear}>
            Clear
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

const ExpectedLifeIndicator = (modality) => {
  const classes = useStyles()
  const [data, setData] = useState()
  const [loading, setLoading] = useState(false)

  const fetch = async () => {
    try {
      setLoading(true)
      const res = await api.modalities.fetchUsageHistory(modality.id)
      setData(res)
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  const onOpen = () => {
    if (data) return
    fetch()
  }

  return (
    <Tooltip
      onOpen={onOpen}
      interactive
      disableFocusListener
      title={
        <div style={{ width: 300 }}>
          <Typography variant="body2">Tube Scan Seconds Stats</Typography>

          <div className={classes.tooltipContentSmall}>
            {!data ? (
              <Typography variant="subtitle1" color="primary">
                Loading data...
              </Typography>
            ) : (
              <div style={{ width: '100%', padding: 16 }}>
                <Box pb={0.5} display="flex" justifyContent="space-between">
                  <Typography variant="body2" className={classes.bold}>
                    Min Scan Secs:
                  </Typography>{' '}
                  <Typography variant="body2" color="textSecondary">
                    {numbro(Number(data.minTubeScanSecs)).format({
                      average: true,
                      lowPrecision: false,
                    })}
                  </Typography>
                </Box>
                <Box pb={0.5} display="flex" justifyContent="space-between">
                  <Typography variant="body2" className={classes.bold}>
                    Max Scan Secs:
                  </Typography>{' '}
                  <Typography variant="body2" color="textSecondary">
                    {numbro(Number(data.maxTubeScanSecs)).format({
                      average: true,
                      lowPrecision: false,
                    })}
                  </Typography>
                </Box>

                <Box pb={0.5} display="flex" justifyContent="space-between">
                  <Typography variant="body2" className={classes.bold}>
                    Standard Deviation:
                  </Typography>{' '}
                  <Typography variant="body2" color="textSecondary">
                    {numbro(Number(data.standardDeviation)).format({
                      average: true,
                      lowPrecision: false,
                    })}
                  </Typography>
                </Box>

                <Box pb={0.5} display="flex" justifyContent="space-between">
                  <Typography variant="body2" className={classes.bold}>
                    Avg for Tube Type:
                  </Typography>{' '}
                  <Typography variant="body2" color="textSecondary">
                    {numbro(Number(modality.tubeLiveAvg)).format({
                      average: true,
                      lowPrecision: false,
                    })}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" className={classes.bold}>
                    Number of Tubes:
                  </Typography>{' '}
                  <Typography variant="body2" color="textSecondary">
                    {data.numTubeReplaced}
                  </Typography>
                </Box>

                <Accordion
                  elevation={0}
                  classes={{
                    root: classes.MuiAccordionRoot,
                  }}
                >
                  <AccordionSummary
                    style={{
                      padding: 0,
                    }}
                    expandIcon={<ExpandMoreIcon fontSize="small" />}
                  >
                    <Typography color="textSecondary" variant="body2">
                      Replacements
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails
                    style={{ display: 'flex', flexDirection: 'column', padding: 0 }}
                  >
                    {data.replacements
                      .sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0))
                      .reverse()
                      .map((replacement) => {
                        return (
                          <Box
                            id={'replacement-' + replacement.id}
                            display="flex"
                            justifyContent="space-between"
                          >
                            {replacement.status === 'Actual' ? (
                              <Fragment>
                                <Typography
                                  variant="body2"
                                  className={classes.bold}
                                  style={{ width: '40%' }}
                                >
                                  <strong>{replacement.serial}</strong>
                                </Typography>{' '}
                                <div
                                  style={{
                                    display: 'flex',
                                    width: '60%',
                                    justifyContent: 'space-between',
                                    marginLeft: 12,
                                  }}
                                >
                                  <Typography variant="body2" color="textSecondary">
                                    <strong>{replacement.date}</strong>
                                  </Typography>
                                  <Typography variant="body2" color="textSecondary">
                                    <strong>Current</strong>
                                  </Typography>
                                </div>
                              </Fragment>
                            ) : (
                              <Fragment>
                                <Typography
                                  variant="body2"
                                  className={classes.bold}
                                  style={{ width: '40%' }}
                                >
                                  {replacement.serial}
                                </Typography>{' '}
                                <div
                                  style={{
                                    display: 'flex',
                                    width: '60%',
                                    justifyContent: 'space-between',
                                    marginLeft: 12,
                                  }}
                                >
                                  <Typography variant="body2" color="textSecondary">
                                    {replacement.date}
                                  </Typography>
                                  <Typography variant="body2" color="textSecondary">
                                    {numbro(Number(replacement.scanSecs)).format({
                                      average: true,
                                      lowPrecision: false,
                                    })}
                                  </Typography>
                                </div>
                              </Fragment>
                            )}
                          </Box>
                        )
                      })}
                  </AccordionDetails>
                </Accordion>
              </div>
            )}
          </div>
        </div>
      }
      arrow
    >
      <div className={classes.indicator}>{modality.tubeExpectedLife}</div>
    </Tooltip>
  )
}

const ChartIndicator = (modality) => {
  const classes = useStyles()
  const [data, setData] = useState()
  const [loading, setLoading] = useState(false)

  const fetch = async () => {
    try {
      setLoading(true)
      const res = await api.modalities.fetchUsageHistory(modality.id)
      const chartData = formatHistoryData(res.history || {})
      setData(chartData)
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  const onOpen = () => {
    if (data) return
    fetch()
  }

  return (
    <Tooltip
      onOpen={onOpen}
      interactive
      disableFocusListener
      title={
        <div style={{ width: 300 }}>
          <Typography variant="body2">Tube Scan Seconds History</Typography>

          <div className={classes.tooltipContent} style={{}}>
            {loading && !data ? (
              <Typography variant="subtitle1" color="primary">
                Loading chart data...
              </Typography>
            ) : (
              <UsageChart data={data} />
            )}
          </div>
        </div>
      }
      arrow
    >
      <div className={classes.indicator}>{modality.tubeScanSecs}</div>
    </Tooltip>
  )
}

const Projection = ({ data }) => {
  const quarters = (data || []).reduce((acc, item) => {
    if (item && item.tubeDateToFail) {
      const quarter = item.tubeDateToFail

      if (item.tubeCost) {
        acc[quarter] = acc[quarter] || 0
        acc[quarter] += numbro.unformat(item.tubeCost)
      }
    }

    return acc
  }, {})

  const list = Object.keys(quarters).sort((a, b) => {
    const [, y1] = a.split(' ')
    const [, y2] = b.split(' ')

    return y1 - y2
  })
  const classes = useStyles()
  const totalCost = list.reduce((sum, key) => sum + (quarters[key] || 0), 0)
  const chartData = list.map((name) => ({ name, value: quarters[name] }))

  return data ? (
    <Box mt={4} mb={4} className={classes.projectionSection}>
      <Card elevation={0} className={classes.projectionCard}>
        <CardContent>
          <Typography className={classes.projectionHeaderEyebrow}>
            Projection Summary
          </Typography>
          <Typography variant="h6" className={classes.projectionTitle}>
            Quarterly Projection
          </Typography>

          <div className={classes.projectionStats}>
            <div>
              <Typography className={classes.projectionStatLabel}>
                Total projected cost
              </Typography>
              <Typography variant="h6" className={classes.projectionStatValue}>
                {numbro(totalCost).formatCurrency()}
              </Typography>
            </div>
            <div>
              <Typography className={classes.projectionStatLabel}>
                Projected quarters
              </Typography>
              <Typography variant="h6" className={classes.projectionStatValue}>
                {list.length}
              </Typography>
            </div>
          </div>

          <Divider />

          {list.length ? (
            <MuiList className={classes.projectionList}>
              {list.map((key, index) => (
                <ListItem
                  key={'quarter-item' + key}
                  className={`${classes.projectionRow} ${index === list.length - 1 ? classes.projectionRowLast : ''}`}
                >
                  <ListItemText
                    primary={key}
                    primaryTypographyProps={{ className: classes.projectionPrimary }}
                  />
                  <ListItemSecondaryAction>
                    <Typography variant="subtitle1" className={classes.projectionStatValue}>
                      {numbro(quarters[key]).formatCurrency()}
                    </Typography>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </MuiList>
          ) : (
            <Typography variant="body2" className={classes.projectionEmpty}>
              No projected tube replacement costs are available for the current result set.
            </Typography>
          )}
        </CardContent>
      </Card>

      <Card elevation={0} className={classes.projectionChartCard}>
        <CardContent>
          <Typography className={classes.projectionHeaderEyebrow}>
            Spend Trend
          </Typography>
          <Typography variant="h6" className={classes.projectionTitle}>
            Cost Projection
          </Typography>

          <div className={classes.projectionChartWrap}>
            <ResponsiveContainer width="100%" height="100%">
              <RechartsBarChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#edf2f7" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: '#6b7280', fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis
                  tick={{ fill: '#6b7280', fontSize: 12 }}
                  tickFormatter={(value) => numbro(value).formatCurrency()}
                  axisLine={false}
                  tickLine={false}
                  width={90}
                />
                <RechartsTooltip
                  cursor={{ fill: 'rgba(25, 118, 210, 0.08)' }}
                  formatter={(value) => [numbro(value).formatCurrency(), 'Cost']}
                  contentStyle={{
                    borderRadius: 10,
                    border: '1px solid #d9e2ec',
                    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
                  }}
                />
                <Bar dataKey="value" fill="#1976d2" radius={[8, 8, 0, 0]} />
              </RechartsBarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </Box>
  ) : null
}

const CustomData = (props) => {
  const transform = (data) => {
    let tubeLiveAvgs = {}

    tubeLiveAvgs = Object.entries(data).reduce((acc, [key, item]) => {
      acc[item.tubePartNumber] = acc[item.tubePartNumber] || []

      if (item.tubeScanSecsAvg) {
        acc[item.tubePartNumber].push(item.tubeScanSecsAvg)
      }

      return acc
    }, {})

    Object.keys(data).forEach((id) => {
      const vals = tubeLiveAvgs[data[id].tubePartNumber]
      const sum = vals.reduce((a, b) => a + b, 0)
      data[id].tubeLiveAvg = sum / vals.length || 0
    })

    return data
  }

  return <Data transform={transform} {...props} />
}

const ModalityList = (props) => {
  const [data, setData] = useState([])
  const classes = useStyles()
  const dataProvider = useDataProvider()
  const accountId = storage.get('user', {})?.accountId
  const [tagTypes, setTagTypes] = useState([])
  const [tagOptions, setTagOptions] = useState({})
  const [draftFilters, setDraftFilters] = useState({})
  const [appliedFilters, setAppliedFilters] = useState({})
  const [visibleOptionalFilters, setVisibleOptionalFilters] = useState([])

  useEffect(() => {
    const fetchTagFilters = async () => {
      try {
        const { data: tagTypeData } = await dataProvider.getList('tag-types', {
          pagination: { page: 1, perPage: 1000 },
          sort: { field: 'name', order: 'ASC' },
          filter: accountId ? { accountId } : {},
        })

        setTagTypes(tagTypeData || [])

        const tagResponses = await Promise.all(
          (tagTypeData || []).map(async (tagType) => {
            const { data: tags } = await dataProvider.getList('tags', {
              pagination: { page: 1, perPage: 1000 },
              sort: { field: 'name', order: 'ASC' },
              filter: accountId
                ? { tagTypeId: tagType.id, accountId }
                : { tagTypeId: tagType.id },
            })

            return [tagType.id, tags || []]
          })
        )

        setTagOptions(Object.fromEntries(tagResponses))
      } catch (error) {
        console.error('Error loading tube analysis filters:', error)
      }
    }

    fetchTagFilters()
  }, [accountId, dataProvider])

  const handleDraftChange = (key, value) => {
    setDraftFilters((prev) => ({
      ...prev,
      [key]: value,
    }))
  }

  const handleApplyFilters = () => {
    const nextFilters = normalizeFilters(draftFilters)
    setAppliedFilters(nextFilters)
    if (!hasAppliedFilter(nextFilters)) {
      setData([])
    }
  }

  const handleClearFilters = () => {
    setDraftFilters({})
    setAppliedFilters({})
    setVisibleOptionalFilters([])
    setData([])
  }

  const handleAddOptionalFilter = (source) => {
    setVisibleOptionalFilters((prev) =>
      prev.includes(source) ? prev : [...prev, source]
    )
  }

  const handleRemoveOptionalFilter = (source) => {
    setVisibleOptionalFilters((prev) => prev.filter((item) => item !== source))
    setDraftFilters((prev) => {
      const next = { ...prev }
      delete next[source]
      return next
    })
  }

  const shouldLoadResults = hasAppliedFilter(appliedFilters)

  return (
    <>
      <FilterPanel
        draftFilters={draftFilters}
        onDraftChange={handleDraftChange}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
        tagTypes={tagTypes}
        tagOptions={tagOptions}
        visibleOptionalFilters={visibleOptionalFilters}
        onAddOptionalFilter={handleAddOptionalFilter}
        onRemoveOptionalFilter={handleRemoveOptionalFilter}
      />

      {shouldLoadResults ? (
        <>
          <List
            key={JSON.stringify(appliedFilters)}
            title="Modality Tube Analysis"
            exporter={exporter}
            bulkActionButtons={false}
            basePath="/modalities/tube-analysis"
            resource="modalities/tube-analysis"
            filter={appliedFilters}
            perPage={25}
            pagination={<Pagination rowsPerPageOptions={[10, 25, 50, 100]} />}
            {...props}
          >
            <CustomData onData={setData}>
              <Datagrid
                size="small"
                rowStyle={(record, index) => ({
                  opacity: record.timeStatusColor === 'red' ? 0.5 : 1,
                })}
              >
                <TextField label="Modality" source="displayName" />

                <FunctionField
                  label="Tube"
                  render={(data) => `${data.tube} (${data.tubePartNumber})`}
                />

                <FunctionField
                  label="Scan Secs"
                  source="tubeScanSecs"
                  render={(props) => <ChartIndicator {...props} />}
                />
                <TextField label="Usage (/mo)" source="tubeScanSecsUsed" />

                <FunctionField
                  label="Expected Life"
                  source="tubeExpectedLife"
                  render={(props) => <ExpectedLifeIndicator {...props} />}
                />

                <TextField label="Time to Fail" source="tubeTimeToFail" />
                <TextField label="Date to Fail" source="tubeDateToFail" />
                <TextField label="Cost" source="tubeCost" />
              </Datagrid>
            </CustomData>
          </List>

          <Projection data={data} />
        </>
      ) : (
        <Card elevation={1} className={classes.filterPanel}>
          <CardContent>
            <Typography variant="body1">
              Apply at least one filter to load tube analysis results.
            </Typography>
          </CardContent>
        </Card>
      )}
    </>
  )
}

export default ModalityList
